/**
 * Masters → Registrations — GST (one per state) plus TAN / PF / ESI / PT.
 * @file src/web/features/admin/components/masters/RegistrationsTab.tsx
 *
 * The GST list is the important one: a return is filed PER GSTIN, so a
 * multi-state client holds several and one of them must be marked primary (that
 * is the one the older screens still read). Adding a GSTIN derives its state
 * from the leading two digits — the state is never typed, so it cannot disagree
 * with the number.
 *
 * Annual turnover sits here rather than on the company, because GSTR-1's `gt`
 * header is per registration and is mandatory in the first filing of a
 * financial year.
 */

import { useCallback, useEffect, useState } from 'react';
import { Landmark, Star, Trash2, Pencil, ShieldCheck, Search, Loader2, Check } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { adminFetch, adminFetchJson } from '../../lib/admin-api';
import {
  Card, Field, TextInput, Select, PrimaryBtn, GhostBtn, AddToggle, FormError,
  Loading, ErrBox, EmptyState, Th, Td, Pill, useEntityResource, mutate, useDeleteConfirm,
} from './shared';

interface GstRegistration {
  id: string; gstin: string; stateCode: string; stateName: string | null;
  legalName: string | null; tradeName: string | null; registrationType: string | null;
  constitutionType: string | null; status: string; isPrimary: boolean;
  filingFrequency: string; portalUsername: string | null;
  registeredAddress: string | null; city: string | null; pincode: string | null;
  annualTurnover: string | null; annualTurnoverFy: string | null;
  eInvoiceApplicable: boolean | null; eInvoiceApplicableFrom: string | null;
  eInvoiceStatusPortal: string | null;
  lutNumber: string | null; lutFy: string | null;
  additionalPlaces: { building?: string | null; locality?: string | null; city?: string | null; state?: string | null; pincode?: string | null }[] | null;
}
interface StatutoryRegistration {
  id: string; kind: string; number: string; stateCode: string | null; stateName: string | null;
  authority: string | null; status: string;
}
interface Meta {
  statutoryKinds: string[];
  gstRegistrationTypes: string[];
  filingFrequencies: string[];
}

export function RegistrationsTab({ entityId }: { entityId: string | null }) {
  const [meta, setMeta] = useState<Meta | null>(null);
  useEffect(() => {
    adminFetchJson<Meta>('/api/masters/meta').then(setMeta).catch(() => setMeta(null));
  }, []);

  return (
    <>
      <EntityIdentity entityId={entityId} meta={meta} />
      <AdditionalInfo entityId={entityId} />
      <StatutoryRegistrationsCard entityId={entityId} meta={meta} />
    </>
  );
}

/* ── entity statutory identity ──────────────────────────────────────────── */

interface Identity {
  id: string; name: string; legalName: string | null;
  panNumber: string | null; cin: string | null;
  registeredAddress: string | null;
  billingAddress: string | null; city: string | null; state: string | null; pincode: string | null;
  complianceModules: Record<string, boolean> | null;
  tanNumbers: string[];
  tradeName: string | null; entityType: string | null;
  incorporationDate: string | null; udyamNumber: string | null;
  booksOfAccountMethod: string | null;
  authorizedCapital: string | null; paidUpCapital: string | null;
}

/** The client's own list, verbatim from the modification sheet (row 4). */
const ENTITY_TYPES = [
  'Proprietorship', 'Partnership', 'LLP', 'Private company', 'Public company',
  'OPC', 'HUF', 'Trust', 'AOP-BOI',
];

/** Entity types that actually have share capital — the rest leave it blank. */
const HAS_CAPITAL = ['Private company', 'Public company', 'OPC'];

/** '2020-06-01T00:00:00.000Z' -> '2020-06-01' for a date input. */
const dateInput = (v: string | null) => (v ? String(v).slice(0, 10) : '');

/**
 * PAN, CIN, registered address. Applicability of each compliance module now
 * lives on its own registration row below, not here.
 */
function EntityIdentity({ entityId, meta }: { entityId: string | null; meta: Meta | null }) {
  const [d, setD] = useState<Identity | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    legalName: '', tradeName: '', entityType: '',
    panNumber: '', cin: '',
    registeredAddress: '', billingAddress: '', city: '', state: '', pincode: '',
  });
  // Client sheet row 8. Derived rather than stored: a `billingSameAsRegistered`
  // column would mean billingAddress could sit empty in the database and have
  // to be resolved at read time by every consumer that prints an address.
  // Ticking the box copies the value instead, so what is stored is always the
  // real address and nothing downstream has to know this checkbox exists.
  const [sameAsRegistered, setSameAsRegistered] = useState(false);
  void meta;

  const load = useCallback(() => {
    if (!entityId) { setErr('Select an entity in the header.'); return; }
    adminFetchJson<Identity>('/api/masters/identity', { query: { entityId } })
      .then((x) => {
        setD(x); setErr(null);
        setF({
          legalName: x.legalName ?? '', tradeName: x.tradeName ?? '',
          entityType: x.entityType ?? '',
          panNumber: x.panNumber ?? '',
          cin: x.cin ?? '', registeredAddress: x.registeredAddress ?? '',
          billingAddress: x.billingAddress ?? '', city: x.city ?? '',
          state: x.state ?? '', pincode: x.pincode ?? '',
        });
        // Only tick when there is a registered address to match — two empty
        // strings are equal, and that is not the user saying "same as".
        setSameAsRegistered(!!x.registeredAddress && x.billingAddress === x.registeredAddress);
      })
      .catch((e: Error) => setErr(e.message));
  }, [entityId]);
  useEffect(load, [load]);

  if (err) return <ErrBox msg={err} />;
  if (!d) return <Loading label="Loading identity" />;

  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true); setFormErr(null);
    try {
      await mutate('identity', 'PATCH', {
        entityId, ...f,
        billingAddress: sameAsRegistered ? f.registeredAddress : f.billingAddress,
      });
      toast.success('Profile saved'); load();
    } catch (e) { setFormErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Card
      title="Entity profile"
      subtitle="Who this client is. PAN and CIN are singular per legal person and sit here; TAN is not, so it lives with the registrations below alongside the GSTINs."
    >
      <div className="px-6 py-5">
        <FormError msg={formErr} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Legal name"><TextInput value={f.legalName} onChange={set('legalName')} /></Field>
          <Field label="Trade name" hint="The name the business trades under. Each GSTIN carries its own, which is what a return shows.">
            <TextInput value={f.tradeName} onChange={set('tradeName')} />
          </Field>
          <Field label="Constitution / entity type">
            <Select value={f.entityType} onChange={set('entityType')} options={ENTITY_TYPES} placeholder="Not recorded" />
          </Field>
          <Field label="PAN" hint="Cross-checked against the primary GSTIN, which embeds it at characters 3-12.">
            <TextInput value={f.panNumber} onChange={(v) => set('panNumber')(v.toUpperCase())} mono />
          </Field>
          <Field label="CIN" hint="Companies only — leave blank for a firm or proprietorship.">
            <TextInput value={f.cin} onChange={(v) => set('cin')(v.toUpperCase())} mono />
          </Field>
          <Field label="TAN" hint="Managed below — a client may hold more than one.">
            <div className="rounded-xl border border-ink-100 bg-ink-50 px-3 py-2 font-mono text-sm text-ink-600">
              {d.tanNumbers.length ? d.tanNumbers.join(', ') : <span className="font-sans text-ink-400">none recorded</span>}
            </div>
          </Field>
        </div>

        <p className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wide text-ink-400">Address</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2">
            <Field label="Registered office" hint="Per-GSTIN addresses are set on each registration below.">
              <TextInput value={f.registeredAddress} onChange={set('registeredAddress')} />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="text-xs font-medium text-ink-600">Billing address</label>
                <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-ink-500">
                  <input
                    type="checkbox"
                    checked={sameAsRegistered}
                    onChange={(e) => setSameAsRegistered(e.target.checked)}
                  />
                  Same as registered office
                </label>
              </div>
              {sameAsRegistered ? (
                <div className="rounded-xl border border-ink-100 bg-ink-50 px-3 py-2 text-sm text-ink-600">
                  {f.registeredAddress || <span className="text-ink-400">enter the registered office first</span>}
                </div>
              ) : (
                <TextInput value={f.billingAddress} onChange={set('billingAddress')} />
              )}
              {sameAsRegistered && (
                <p className="text-[11px] text-ink-400">Saved as a copy, so it stays correct if this is ever unticked.</p>
              )}
            </div>
          </div>
          <Field label="City"><TextInput value={f.city} onChange={set('city')} /></Field>
          <Field label="State"><TextInput value={f.state} onChange={set('state')} /></Field>
          <Field label="Pincode"><TextInput value={f.pincode} onChange={set('pincode')} /></Field>
        </div>

        {/* The compliance-module switches used to sit here, separated from the
            registrations they govern — so "is this client subject to PF?" was
            answered in one card and "what is their PF code?" in another, and
            clicking a switch appeared to do nothing. Applicability now lives on
            each registration row below, where the number is entered. */}

        <div className="mt-6"><PrimaryBtn onClick={save} busy={busy}>Save profile</PrimaryBtn></div>
      </div>
    </Card>
  );
}

/* ── additional info ────────────────────────────────────────────────────── */

/**
 * The entity facts that are not its identity: when it came into existence, its
 * MSME registration, how it keeps its books and what capital it has.
 *
 * Split out of the profile card because that card had grown to twelve fields
 * and two sub-blocks. These five are read far less often than the name, PAN
 * and address above, so they fold away by default.
 *
 * Fetches and PATCHes the same /identity endpoint as the profile card. The
 * service only writes the keys present in the body, so the two cards saving
 * independently cannot clobber each other's fields.
 */
function AdditionalInfo({ entityId }: { entityId: string | null }) {
  const [d, setD] = useState<Identity | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    incorporationDate: '', udyamNumber: '',
    booksOfAccountMethod: '', authorizedCapital: '', paidUpCapital: '',
  });

  const load = useCallback(() => {
    if (!entityId) { setErr('Select an entity in the header.'); return; }
    adminFetchJson<Identity>('/api/masters/identity', { query: { entityId } })
      .then((x) => {
        setD(x); setErr(null);
        setF({
          incorporationDate: dateInput(x.incorporationDate),
          udyamNumber: x.udyamNumber ?? '',
          booksOfAccountMethod: x.booksOfAccountMethod ?? '',
          authorizedCapital: x.authorizedCapital ?? '',
          paidUpCapital: x.paidUpCapital ?? '',
        });
      })
      .catch((e: Error) => setErr(e.message));
  }, [entityId]);
  useEffect(load, [load]);

  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true); setFormErr(null);
    try {
      await mutate('identity', 'PATCH', { entityId, ...f });
      toast.success('Additional info saved'); load();
    } catch (e) { setFormErr((e as Error).message); } finally { setBusy(false); }
  };

  const filled = Object.values(f).filter((v) => String(v).trim() !== '').length;
  const summary = err ? 'could not load'
    : !d ? 'loading…'
    : filled === 0 ? '— nothing recorded'
    : `· ${filled} of 5 recorded`;

  // Capital is a company concept; the hint says so rather than the field
  // vanishing, so a firm that later incorporates still has somewhere to type it.
  const capitalHint = d?.entityType && !HAS_CAPITAL.includes(d.entityType)
    ? `Not applicable to a ${d.entityType.toLowerCase()}.`
    : 'Companies only.';

  return (
    <Card
      collapsible
      defaultOpen={false}
      summary={summary}
      title="Additional info"
      subtitle="Incorporation, MSME registration, method of accounting and share capital."
    >
      {err && <div className="px-6 py-5"><ErrBox msg={err} /></div>}
      {!d && !err && <Loading label="Loading additional info" />}

      {d && !err && (
        <div className="px-6 py-5">
          <FormError msg={formErr} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Date of incorporation" hint="Or commencement of business.">
              <TextInput type="date" value={f.incorporationDate} onChange={set('incorporationDate')} />
            </Field>
            <Field label="Udyam / MSME no" hint="This client's own MSME registration, not a supplier's.">
              <TextInput value={f.udyamNumber} onChange={(v) => set('udyamNumber')(v.toUpperCase())} mono />
            </Field>
            <Field label="Method of accounting" hint="The ITR answer — mercantile (accrual) or cash.">
              <Select
                value={f.booksOfAccountMethod}
                onChange={set('booksOfAccountMethod')}
                options={[{ value: 'mercantile', label: 'Mercantile (accrual)' }, { value: 'cash', label: 'Cash' }]}
                placeholder="Not recorded"
              />
            </Field>
            <div />
            <Field label="Authorised capital" hint={capitalHint}>
              <TextInput value={f.authorizedCapital} onChange={set('authorizedCapital')} mono />
            </Field>
            <Field label="Paid-up capital" hint="Cannot exceed authorised capital.">
              <TextInput value={f.paidUpCapital} onChange={set('paidUpCapital')} mono />
            </Field>
          </div>
          <div className="mt-6"><PrimaryBtn onClick={save} busy={busy}>Save additional info</PrimaryBtn></div>
        </div>
      )}
    </Card>
  );
}

/* ── all statutory registrations, GST included ──────────────────────────── */

/**
 * GST and the rest in one card. They were two, which forced the second to be
 * called "Other statutory registrations" — a name that only made sense
 * relative to the card above it. GST is a statutory registration like any
 * other; it just carries more per-registration detail, so it keeps the richer
 * table while TAN/PF/ESI/PT/IEC stay a row-per-kind checklist.
 *
 * The two blocks load independently and report a summary up, so the collapsed
 * bar can say something about both without either fetching for the other.
 */
function StatutoryRegistrationsCard({ entityId, meta }: { entityId: string | null; meta: Meta | null }) {
  const [gstSummary, setGstSummary] = useState('');
  const [statSummary, setStatSummary] = useState('');
  const summary = [gstSummary, statSummary].filter(Boolean).join(' · ');

  return (
    <Card
      collapsible
      defaultOpen={false}
      summary={summary}
      title="Statutory registrations"
      subtitle="Every registration this client could hold — GST per state, plus TAN, PF, ESI, Professional Tax and IEC. A blank row means nobody has recorded it yet; mark it not applicable if the client is not subject to it and the gap report will stop asking."
    >
      <div className="divide-y divide-ink-100">
        <GstRegistrations entityId={entityId} meta={meta} onSummary={setGstSummary} />
        <StatutoryRegistrations entityId={entityId} meta={meta} onSummary={setStatSummary} />
      </div>
    </Card>
  );
}

function GstRegistrations({ entityId, meta, onSummary }: {
  entityId: string | null; meta: Meta | null; onSummary: (s: string) => void;
}) {
  const { items, loading, error, reload } = useEntityResource<GstRegistration>('gst-registrations', entityId);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<GstRegistration | null>(null);
  const { askDelete, deleteDialog } = useDeleteConfirm();

  const act = async (fn: () => Promise<void>, okMsg: string) => {
    try { await fn(); toast.success(okMsg); reload(); }
    catch (e) { toast.error((e as Error).message); }
  };

  const primary = items.find((r) => r.isPrimary);
  const summary = loading ? 'GST loading…'
    : error ? 'GST could not load'
    : items.length === 0 ? 'no GSTIN on file'
    : `${items.length} ${items.length === 1 ? 'GSTIN' : 'GSTINs'}${primary ? `, primary ${primary.stateName ?? primary.stateCode}` : ', no primary set'}`;
  useEffect(() => onSummary(summary), [summary, onSummary]);

  return (
    <div className="py-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 px-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">GST</p>
        {!loading && !error && (
          <AddToggle open={adding} onToggle={() => { setAdding(!adding); setEditing(null); }} label="Add GSTIN" />
        )}
      </div>

      {loading && <Loading label="Loading GST registrations" />}
      {error && <div className="px-6 py-5"><ErrBox msg={error} /></div>}

      {!loading && !error && <>
      {adding && (
        <GstForm
          meta={meta}
          onCancel={() => setAdding(false)}
          onSave={async (body) => {
            await mutate('gst-registrations', 'POST', { entityId, ...body });
            setAdding(false); toast.success('Registration added'); reload();
          }}
        />
      )}

      {editing && (
        <GstForm
          meta={meta}
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={async (body) => {
            await mutate(`gst-registrations/${editing.id}`, 'PATCH', { entityId, ...body });
            setEditing(null); toast.success('Registration updated'); reload();
          }}
        />
      )}

      {items.length === 0 && !adding ? (
        <EmptyState
          what="GST registration"
          why="A return is filed per GSTIN, so nothing can be prepared until at least one is on file. Add the client's GSTIN and the state is derived from it."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <Th>GSTIN</Th><Th>State</Th><Th>Type</Th><Th>Filing</Th><Th right>Annual turnover</Th><Th>Status</Th><Th right>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {items.map((r) => (
                <tr key={r.id} className="hover:bg-ink-50/50">
                  <Td mono>
                    <span className="inline-flex items-center gap-2">
                      {r.gstin}
                      {r.isPrimary && <Pill tone="brand"><Star className="h-3 w-3" /> Primary</Pill>}
                    </span>
                  </Td>
                  <Td>{r.stateName ?? r.stateCode}</Td>
                  <Td muted>{r.registrationType ?? '—'}</Td>
                  <Td muted>{r.filingFrequency === 'quarterly' ? 'Quarterly (QRMP)' : 'Monthly'}</Td>
                  <Td right mono>
                    {r.annualTurnover
                      ? <>₹{Number(r.annualTurnover).toLocaleString('en-IN')}<span className="ml-1 text-ink-400">{r.annualTurnoverFy}</span></>
                      : <span className="text-warn-600">not set</span>}
                  </Td>
                  <Td>
                    <Pill tone={r.status === 'active' ? 'ok' : 'danger'}>{r.status}</Pill>
                  </Td>
                  <Td right>
                    <span className="inline-flex gap-1.5">
                      {!r.isPrimary && (
                        <GhostBtn onClick={() => act(() => mutate(`gst-registrations/${r.id}/primary`, 'POST', { entityId }), 'Primary registration changed')}>
                          <Star className="h-3 w-3" /> Make primary
                        </GhostBtn>
                      )}
                      <GhostBtn onClick={() => { setEditing(r); setAdding(false); }}><Pencil className="h-3 w-3" /> Edit</GhostBtn>
                      <GhostBtn
                        tone="danger"
                        onClick={() => askDelete(
                          `GSTIN ${r.gstin}`,
                          () => act(() => mutate(`gst-registrations/${r.id}`, 'DELETE', { entityId }), 'Registration removed'),
                        )}
                      >
                        <Trash2 className="h-3 w-3" />
                      </GhostBtn>
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>}
      {deleteDialog}
    </div>
  );
}

function GstForm({ initial, meta, onSave, onCancel }: {
  initial?: GstRegistration; meta: Meta | null;
  onSave: (body: Record<string, unknown>) => Promise<void>; onCancel: () => void;
}) {
  const [gstin, setGstin] = useState(initial?.gstin ?? '');
  const [registrationType, setRegistrationType] = useState(initial?.registrationType ?? 'Regular');
  const [filingFrequency, setFilingFrequency] = useState(initial?.filingFrequency ?? 'monthly');
  const [portalUsername, setPortalUsername] = useState(initial?.portalUsername ?? '');
  const [legalName, setLegalName] = useState(initial?.legalName ?? '');
  const [registeredAddress, setRegisteredAddress] = useState(initial?.registeredAddress ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [pincode, setPincode] = useState(initial?.pincode ?? '');
  const [eInvoiceApplicable, setEInvoiceApplicable] = useState<boolean | null>(initial?.eInvoiceApplicable ?? null);
  const [eInvoiceApplicableFrom, setEInvoiceApplicableFrom] = useState(dateInput(initial?.eInvoiceApplicableFrom ?? null));
  const [eInvoiceStatusPortal, setEInvoiceStatusPortal] = useState(initial?.eInvoiceStatusPortal ?? '');
  const [lutNumber, setLutNumber] = useState(initial?.lutNumber ?? '');
  const [lutFy, setLutFy] = useState(initial?.lutFy ?? '');
  const [additionalPlaces, setAdditionalPlaces] = useState(initial?.additionalPlaces ?? null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);

  /** Fill the registration from the GST portal so it is not typed from a PDF. */
  const lookup = async () => {
    const g = gstin.trim().toUpperCase();
    if (g.length !== 15) { toast.error('Enter a 15-character GSTIN first'); return; }
    setLooking(true);
    try {
      const res = await adminFetch(`/api/gst/lookup/${g}`);
      const b = await res.json().catch(() => ({}));
      if (!res.ok || !b.success) throw new Error(b?.error || 'Lookup failed');
      const d = b.data;
      setLegalName(d.legalName ?? d.tradeName ?? legalName);
      setRegistrationType(d.businessType ?? registrationType);
      setRegisteredAddress(d.address?.full ?? registeredAddress);
      setCity(d.address?.city ?? city);
      setPincode(d.address?.pincode ?? pincode);
      // The lookup has always parsed these three and thrown them away.
      if (d.eInvoiceStatus) setEInvoiceStatusPortal(String(d.eInvoiceStatus));
      if (Array.isArray(d.additionalAddresses) && d.additionalAddresses.length) {
        setAdditionalPlaces(d.additionalAddresses);
      }
      toast.success('Filled from the GST portal');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Lookup failed');
    } finally { setLooking(false); }
  };

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await onSave({
        ...(initial ? {} : { gstin: gstin.trim().toUpperCase() }),
        registrationType, filingFrequency,
        portalUsername: portalUsername.trim() || null,
        legalName: legalName || null,
        registeredAddress: registeredAddress || null,
        city: city || null, pincode: pincode || null,
        eInvoiceApplicable,
        eInvoiceApplicableFrom: eInvoiceApplicableFrom || null,
        eInvoiceStatusPortal: eInvoiceStatusPortal || null,
        lutNumber: lutNumber || null,
        lutFy: lutFy || null,
        additionalPlaces,
      });
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-b border-ink-100 bg-ink-50/60 px-6 py-5">
      <FormError msg={err} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="GSTIN" hint={initial ? 'Not editable — filed returns reference it. Delete and re-add instead.' : 'The state is derived from the first two digits.'}>
          <div className="flex gap-2">
            <TextInput value={gstin} onChange={(v) => setGstin(v.toUpperCase())} placeholder="29ABCDE1234F1Z5" mono />
            {!initial && (
              <button
                onClick={lookup}
                disabled={looking}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-ink-300 bg-white px-3 text-xs font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-60"
              >
                {looking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                Look up
              </button>
            )}
          </div>
        </Field>
        <Field label="Registration type">
          <Select value={registrationType} onChange={setRegistrationType} options={meta?.gstRegistrationTypes ?? ['Regular']} />
        </Field>
        <Field label="GST-portal username" hint="The client's login ID for THIS GSTIN — needed to send them a filing OTP. Never their password: the API has no password field, and we should not hold one.">
          <TextInput value={portalUsername} onChange={setPortalUsername} />
        </Field>
        <Field label="Filing frequency" hint="Quarterly = QRMP scheme.">
          <Select value={filingFrequency} onChange={setFilingFrequency} options={meta?.filingFrequencies ?? ['monthly', 'quarterly']} />
        </Field>
        <Field label="Legal name">
          <TextInput value={legalName} onChange={setLegalName} placeholder="As registered" />
        </Field>
        <Field label="Registered address" hint="Drives place-of-supply and jurisdiction; differs per state.">
          <TextInput value={registeredAddress} onChange={setRegisteredAddress} />
        </Field>
        <Field label="City"><TextInput value={city} onChange={setCity} /></Field>
        <Field label="Pincode"><TextInput value={pincode} onChange={setPincode} /></Field>
      </div>

      {/* ── e-invoicing, LUT and additional places (sheet rows 12, 13, 7) ──
          Recorded, not acted on: this app files returns from Tally data and
          does not call the IRP. The client e-invoices elsewhere, and we hold
          the fact so a period can be checked against it. */}
      <p className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wide text-ink-400">
        E-invoicing &amp; exports
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="E-invoice applicable" hint="Recorded for reference — this app does not generate IRNs.">
          <Select
            value={eInvoiceApplicable === null || eInvoiceApplicable === undefined ? '' : eInvoiceApplicable ? 'yes' : 'no'}
            onChange={(v) => setEInvoiceApplicable(v === '' ? null : v === 'yes')}
            options={[{ value: 'yes', label: 'Applicable' }, { value: 'no', label: 'Not applicable' }]}
            placeholder="Not decided"
          />
        </Field>
        <Field label="Applicable from" hint="Whether an invoice needed an IRN depends on its date, so a yes alone will not answer it later.">
          <TextInput type="date" value={eInvoiceApplicableFrom} onChange={setEInvoiceApplicableFrom} />
        </Field>
        <Field label="Portal says" hint="What the GST portal reported at the last lookup. Read-only.">
          <div className="rounded-xl border border-ink-100 bg-ink-50 px-3 py-2 text-sm text-ink-600">
            {eInvoiceStatusPortal || <span className="text-ink-400">not fetched</span>}
          </div>
        </Field>
        <Field label="LUT number" hint="ARN of the furnished LUT — for exports without payment of tax.">
          <TextInput value={lutNumber} onChange={(v) => setLutNumber(v.toUpperCase())} mono />
        </Field>
        <Field label="LUT financial year" hint="An LUT lapses with the year it was furnished for.">
          <TextInput value={lutFy} onChange={setLutFy} placeholder="2025-26" />
        </Field>
        <Field label="Additional places of business" hint="Filled from the GST portal by the lookup above.">
          <div className="rounded-xl border border-ink-100 bg-ink-50 px-3 py-2 text-sm text-ink-600">
            {additionalPlaces?.length
              ? `${additionalPlaces.length} on file`
              : <span className="text-ink-400">none recorded</span>}
          </div>
        </Field>
      </div>
      {additionalPlaces && additionalPlaces.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-ink-500">
          {additionalPlaces.map((a, i) => (
            <li key={i}>
              · {[a.building, a.locality, a.city, a.state, a.pincode].filter(Boolean).join(', ')}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 flex items-center gap-3">
        <PrimaryBtn onClick={submit} busy={busy}>{initial ? 'Save changes' : 'Add registration'}</PrimaryBtn>
        <button onClick={onCancel} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
      </div>
    </div>
  );
}

/* ── TAN / PF / ESI / PT / IEC ──────────────────────────────────────────── */

/**
 * One row per statutory registration this client could hold, always shown —
 * empty rows are the point. A blank PF row says "nobody has recorded this",
 * which a hidden section never could.
 *
 * Each row carries its own applicability instead of a switch panel elsewhere on
 * the page, because a blank field and an inapplicable field look identical and
 * only the person entering the number knows which it is. Three states:
 * recorded — a number is on file
 * not recorded — expected, still missing (a gap)
 * not applicable — deliberately excluded, and the gap report stops asking
 *
 * Applicability writes `complianceModules` on the entity, the same flag the
 * gap report already reads. IEC has no module — nothing gates on it — so it is
 * simply optional.
 */
const KINDS: { kind: string; label: string; module: string | null; help: string }[] = [
  { kind: 'TAN', label: 'TAN', module: 'tds', help: 'Required to file any TDS return or generate a challan.' },
  { kind: 'PF', label: 'PF establishment code', module: 'pf', help: 'Issued by EPFO. Applies above the employee-count threshold.' },
  { kind: 'ESI', label: 'ESI code', module: 'esi', help: 'Employer code issued by ESIC.' },
  { kind: 'PT', label: 'Professional Tax', module: 'pt', help: 'Legislated per state — a multi-state employer holds one per state.' },
  { kind: 'IEC', label: 'IEC', module: null, help: 'Importer-Exporter Code. Optional — only for clients trading across the border.' },
];

function StatutoryRegistrations({ entityId, meta, onSummary }: {
  entityId: string | null; meta: Meta | null; onSummary: (s: string) => void;
}) {
  const { items, loading, error, reload } = useEntityResource<StatutoryRegistration>('statutory-registrations', entityId);
  const [modules, setModules] = useState<Record<string, boolean> | null>(null);
  const [addFor, setAddFor] = useState<string | null>(null);
  const [number, setNumber] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [authority, setAuthority] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { askDelete, deleteDialog } = useDeleteConfirm();
  void meta;

  const loadModules = useCallback(() => {
    if (!entityId) return;
    adminFetchJson<{ complianceModules: Record<string, boolean> | null }>('/api/masters/identity', { query: { entityId } })
      .then((x) => setModules(x.complianceModules ?? {}))
      .catch(() => setModules({}));
  }, [entityId]);
  useEffect(loadModules, [loadModules]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await mutate('statutory-registrations', 'POST', {
        entityId, kind: addFor, number: number.trim().toUpperCase(),
        stateCode: stateCode || null, authority: authority || null,
      });
      setAddFor(null); setNumber(''); setStateCode(''); setAuthority('');
      toast.success(`${addFor} recorded`); reload();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const remove = async (id: string) => {
    try { await mutate(`statutory-registrations/${id}`, 'DELETE', { entityId }); toast.success('Removed'); reload(); }
    catch (e) { toast.error((e as Error).message); }
  };

  // Writes the whole map back — the PATCH replaces it wholesale, so a partial
  // object would silently clear the other decisions.
  const setApplicable = async (mod: string, applies: boolean) => {
    const next = { ...(modules ?? {}), [mod]: applies };
    setModules(next);
    try {
      await mutate('identity', 'PATCH', { entityId, complianceModules: next });
      toast.success(applies ? 'Marked applicable' : 'Marked not applicable');
    } catch (e) { toast.error((e as Error).message); loadModules(); }
  };

  const decided = KINDS.filter((k) => k.module).filter((k) => modules && k.module! in modules).length;
  const totalDecidable = KINDS.filter((k) => k.module).length;
  const summary = loading || modules === null ? ''
    : error ? 'others could not load'
    : decided < totalDecidable ? `${totalDecidable - decided} undecided`
    : `${items.length} other recorded`;
  useEffect(() => onSummary(summary), [summary, onSummary]);

  return (
    <div className="py-5">
      <div className="mb-3 px-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">TAN, PF, ESI, PT &amp; IEC</p>
      </div>

      {(loading || modules === null) && <Loading label="Loading statutory registrations" />}
      {error && <div className="px-6 py-5"><ErrBox msg={error} /></div>}

      {!loading && !error && modules !== null && (
        <div className="divide-y divide-ink-100">
          {KINDS.map(({ kind, label, module, help }) => {
            const rows = items.filter((s) => s.kind === kind);
            const na = module ? modules[module] === false : false;
            const undecided = module ? !(module in modules) : false;

            return (
              <div key={kind} className={`px-6 py-5 ${na ? 'bg-ink-50/60' : ''}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-sm font-semibold ${na ? 'text-ink-400' : 'text-ink-800'}`}>{label}</span>
                      {rows.length > 0
                        ? <Pill tone="ok"><Check className="h-3 w-3" />recorded</Pill>
                        : na
                          ? <Pill tone="neutral">not applicable</Pill>
                          : undecided
                            ? <Pill tone="warn">not recorded — undecided</Pill>
                            : <Pill tone="danger">not recorded</Pill>}
                    </div>
                    <p className={`mt-1 max-w-2xl text-xs ${na ? 'text-ink-400' : 'text-ink-500'}`}>{help}</p>

                    {rows.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {rows.map((s) => (
                          <li key={s.id} className="flex flex-wrap items-center gap-2 text-sm">
                            <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-ink-400" aria-hidden />
                            <span className="font-mono text-ink-700">{s.number}</span>
                            {s.stateName && <span className="text-ink-500">· {s.stateName}</span>}
                            {s.authority && <span className="text-ink-400">· {s.authority}</span>}
                            <GhostBtn tone="danger" onClick={() => askDelete(`${kind} ${s.number}`, () => remove(s.id))}>
                              <Trash2 className="h-3 w-3" />
                            </GhostBtn>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                    {!na && (
                      <GhostBtn onClick={() => { setAddFor(addFor === kind ? null : kind); setErr(null); }}>
                        {addFor === kind ? 'Cancel' : rows.length ? 'Add another' : 'Add number'}
                      </GhostBtn>
                    )}
                    {module && (na
                      ? <GhostBtn onClick={() => setApplicable(module, true)}>Mark applicable</GhostBtn>
                      : rows.length === 0 && <GhostBtn onClick={() => setApplicable(module, false)}>Not applicable</GhostBtn>)}
                  </div>
                </div>

                {addFor === kind && (
                  <div className="mt-4 rounded-xl border border-ink-200 bg-ink-50/60 p-4">
                    <FormError msg={err} />
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      <Field label="Number">
                        <TextInput value={number} onChange={(v) => setNumber(v.toUpperCase())} placeholder={kind === 'TAN' ? 'BLRT01234F' : ''} mono />
                      </Field>
                      <Field label="State" hint={kind === 'PT' ? 'Required — PT slabs are state-specific.' : 'Optional'}>
                        <TextInput value={stateCode} onChange={setStateCode} placeholder="Karnataka or 29" />
                      </Field>
                      <Field label="Issuing office"><TextInput value={authority} onChange={setAuthority} /></Field>
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                      <PrimaryBtn onClick={submit} busy={busy}>Add {kind}</PrimaryBtn>
                      <button onClick={() => setAddFor(null)} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {deleteDialog}
    </div>
  );
}


export { Landmark };
